import React, { useContext, useEffect, useState } from 'react';

import { createStyles, makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import { GridRowParams, GridValueFormatterParams } from '@mui/x-data-grid';

import CustomDataGrid from '../../components/data-grid';
import Screen from '../../components/screen';
import SplashWait from '../../components/splash-wait';
import { AppContext } from '../../contexts/app-context';
import GlobalStyles from '../../global-styles';
import {
  canReadSaves, canWriteSaves, checkUserAuthenticated, formatDateForDataGrid, getSavesEntities,
  getSavesEntityValues, getShowDashboardValue, goToRoutePage, setWindowTitle, toggleShowDashboard
} from '../../utils/helper';
import {
  AddFileIcon, AddIcon, DashboardOffIcon, DashboardOnIcon, MoreIcon, RefreshIcon
} from '../../utils/icons';
import {
  Box, Breadcrumbs, Button, Card, CardContent, CardHeader, FormControl, Grid, Hidden, IconButton,
  InputLabel, Link, ListItemIcon, ListItemText, Menu, MenuItem, Select, Tooltip, Typography
} from '../../utils/material-ui-core';
import SavesCRUD from './crud/saves-crud';
import UploadSavesFile from './uploadSavesFile';

const StyledMenuItem = withStyles((theme) => ({
  root: {
    "&:focus": {
      backgroundColor: theme.palette.secondary.main,
      "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
        color: theme.palette.common.white,
      },
    },
  },
}))(MenuItem);

const Saves = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { referenceLookups, writeLog, userFunctionAccess, isUserAuthenticated, showAlert, savesEntityKey, setSavesEntityKey } = useContext(AppContext);

  const [loading, setLoading] = useState<boolean>(true);
  const [showDashboard, setShowDashboard] = useState<boolean>(true);
  const [openFileUploadDialog, setOpenFileUploadDialog] = useState<boolean>(false);
  const [gridMoreAnchorEl, setGridMoreAnchorEl] = React.useState(null);
  const [savesEntities, setSavesEntities] = useState<SavesEntity[]>([]);
  const [showIndicator, setShowIndicator] = useState<number>(1);
  const [savesEntityValues, setSavesEntityValues] = useState<SavesEntityValue[]>([]);
  const [filterSavesEntityValues, setFilterSavesEntityValues] = useState<SavesEntityValue[]>([]);
  const [savesEntityValueExpirationSummaries, setSavesEntityValueExpirationSummaries] = useState<SavesEntityValueExpirationSummary[]>([]);
  const [savesEditDialogOpen, setSavesEditDialogOpen] = useState<boolean>(false);
  const [selectedSavesEntityValueKey, setSelectedSavesEntityValueKey] = useState("");

  const columns = [
    { field: "savesEntityType", headerName: "Entity Type", width: 170, hide: false },
    { field: "savesEntityName", headerName: "Entity Name", width: 300, hide: false },
    { field: "savesTypeName", headerName: "Save Type", width: 170, hide: false },
    { field: "country", headerName: "Country", width: 150, hide: false },
    { field: "regionName", headerName: "Region", width: 150, hide: false },
    { field: "effectiveDate", headerName: "Effective Date", width: 200, type: "date", hide: false, valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "expirationDate", headerName: "Expiration Date", width: 200, type: "date", hide: false, valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "expiredIn", headerName: "Expire In", width: 200, hide: false },
    { field: "alignedValue", headerName: "Aligned", width: 150, hide: false, type: "number" },
    { field: "avgValue", headerName: "Average ", width: 150, hide: false, type: "number" },
    { field: "lowValue", headerName: "Low", width: 150, hide: false, type: "number" },
    { field: "highValue", headerName: "High", width: 150, hide: false, type: "number" },
    { field: "note", headerName: "Note", width: 500, hide: true },
    { field: "active", headerName: "Active", width: 130, hide: false, type: "boolean" },
    { field: "expiryNote", headerName: "Expiry Note", width: 500, hide: true },
    { field: "lastModifiedBy", headerName: "Last Modified By", width: 250, hide: true },
    { field: "lastModifiedDateTime", headerName: "Last Modified Date", width: 200, type: "date", hide: false, valueFormatter: (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string) },
    { field: "savesEntityValueKey", headerName: "Key", width: 350, hide: true },
  ];

  const gotoExpirationList = async (key) => {

    setSavesEntityKey(key);
    setShowIndicator(3);

    await getValues2(key, 3);

  }

  // Open upload file dialog
  const displayFileUploadDialog = () => {
    setOpenFileUploadDialog(true);
  };

  // Confirmation from upload file dialog
  const confirmFileUploadDialog = async (confirmOutput: ConfirmOutput) => {
    setOpenFileUploadDialog(false);
    if (confirmOutput.status === "refresh") {
      await refresh();
    }
  };

  // More menu click event
  const handleGridMoreMenuClick = (event) => {
    setGridMoreAnchorEl(event.currentTarget);
  };

  // Close more menu
  const handleGridMoreMenuClose = () => {
    setGridMoreAnchorEl(null);
  };

  const newRecord = () => {
    setSelectedSavesEntityValueKey(referenceLookups.newRecordDefaultId);
    setSavesEditDialogOpen(true);
  };

  const handleDataGridClick = (params: GridRowParams, event: any) => {
    const filterSavesEntityValue = filterSavesEntityValues.filter((filterSavesEnityValue) => filterSavesEnityValue.savesEntityValueKey === params.row.savesEntityValueKey);
    if (filterSavesEntityValue.length > 0) {
      setSelectedSavesEntityValueKey(filterSavesEntityValue[0].savesEntityValueKey);
      setSavesEditDialogOpen(true);
    }
  };

  const savesEditDialogResult = async (confirmOutput: ConfirmOutput) => {
    setSavesEditDialogOpen(false);
    await getEntites();
    await getValues(savesEntityKey);
  };

  const refresh = async () => {
    await getEntites();
    await getValues(savesEntityKey);
  };

  const getEntites = async () => {
    setSavesEntities([]);
    setSavesEntityValues([]);
    setFilterSavesEntityValues([]);

    setLoading(true);

    const apiResponse = await getSavesEntities();

    if (apiResponse.success) {
      setSavesEntities(apiResponse.data.savesEntities);
    } else {
      showAlert({ message: apiResponse.errorMessage, severity: "error" });
      return;
    }

    setLoading(false);
  };

  const getValues = async (key) => {
    await getValues2(key, showIndicator)
  }


  const getValues2 = async (key, indicator) => {
    setFilterSavesEntityValues([]);

    if (key === "") {
      setLoading(false);
      return;
    }

    setLoading(true);

    const apiResponse = await getSavesEntityValues(key);

    setLoading(false);

    if (apiResponse.success) {
      setSavesEntityValues(apiResponse.data.savesEntityValues);
      filterValues(apiResponse.data.savesEntityValues, indicator);
      setSavesEntityValueExpirationSummaries(apiResponse.data.savesEntityValueExpirationSummaries);
    } else {
      showAlert({ message: apiResponse.errorMessage, severity: "error" });
      return;
    }
  };

  const filterValues = (values: SavesEntityValue[], indicator: number) => {
    setFilterSavesEntityValues([]);
    const rows: any = [];

    if (indicator === 1) {
      values.filter((saveValue) => saveValue.active === true).map((saveValue, index) => rows.push({ id: index, ...saveValue }));
    } else if (indicator === 2) {
      values.filter((saveValue) => saveValue.active === false).map((saveValue, index) => rows.push({ id: index, ...saveValue }));
    } else if (indicator === 3) {
      values.filter((saveValue) => saveValue.active === true && saveValue.expiredIn !== '').map((saveValue, index) => rows.push({ id: index, ...saveValue }));
    } else {
      values.map((saveValue, index) => rows.push({ id: index, ...saveValue }));
    }

    setFilterSavesEntityValues(rows);
  };

  useEffect(() => {
    writeLog("Saves", "", "", "");
    setWindowTitle("Saves");
    window.scrollTo(0, 0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const initialPageAsync = async () => {
      checkUserAuthenticated(isUserAuthenticated);

      if (!canReadSaves(userFunctionAccess)) {
        showAlert({ message: "Access Denied.", severity: "error" });
        goToRoutePage("home");
        return;
      }

      setShowDashboard(getShowDashboardValue("ShowSavesDashboard", true));

      await getEntites();

      setLoading(false);

      getValues(savesEntityKey);
    };
    initialPageAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthenticated]);

  return (
    <React.Fragment>
      {loading ? (
        <SplashWait />
      ) : (
        <Screen>
          <UploadSavesFile open={openFileUploadDialog} savesEntityKey={savesEntityKey} onCallback={confirmFileUploadDialog} />

          <SavesCRUD open={savesEditDialogOpen} savesEntityKey={savesEntityKey} savesEntityValueKey={selectedSavesEntityValueKey} onCallback={savesEditDialogResult} />

          <Box className={pageStyles.root}>
            <Grid container spacing={3}>
              <Grid item xs={9}>
                <Box className={globalStyles.bottomSpacer}>
                  <Breadcrumbs aria-label="breadcrumb">
                    <Link color="inherit" onClick={() => goToRoutePage("home")} className={globalStyles.link}>
                      Home
                    </Link>
                    <Typography color="textPrimary">Saves</Typography>
                  </Breadcrumbs>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Hidden xsDown>
                  <Box display="flex" justifyContent="flex-end" width={1}>
                    <IconButton onClick={() => setShowDashboard(toggleShowDashboard("ShowSavesDashboard", showDashboard))}>
                      <Tooltip arrow title={!showDashboard ? "Show Dashboard" : "Hide Dashboard"}>
                        {!showDashboard ? <DashboardOffIcon color="secondary" /> : <DashboardOnIcon color="secondary" />}
                      </Tooltip>
                    </IconButton>

                    {canWriteSaves(userFunctionAccess) && savesEntityKey !== "" ? (
                      <>
                        <IconButton>
                          <Tooltip arrow title={"New values"}>
                            <AddIcon color="secondary" onClick={newRecord} />
                          </Tooltip>
                        </IconButton>
                        <IconButton>
                          <Tooltip arrow title={"Upload Saves File"}>
                            <AddFileIcon color="secondary" onClick={displayFileUploadDialog} />
                          </Tooltip>
                        </IconButton>
                      </>
                    ) : null}

                    <IconButton>
                      <Tooltip arrow title={"Refresh"}>
                        <RefreshIcon color="secondary" onClick={() => refresh()} />
                      </Tooltip>
                    </IconButton>
                  </Box>
                </Hidden>
                <Hidden smUp>
                  <Box display="flex" justifyContent="flex-end" width={1}>
                    <Tooltip arrow title="Menu">
                      <Button color="secondary" aria-controls="fade-menu" aria-haspopup="true" onClick={handleGridMoreMenuClick}>
                        <MoreIcon />
                      </Button>
                    </Tooltip>
                    <Menu elevation={2} id="grid-menu" anchorEl={gridMoreAnchorEl} keepMounted open={Boolean(gridMoreAnchorEl)} onClose={handleGridMoreMenuClose}>
                      <StyledMenuItem
                        onClick={(e) => {
                          setShowDashboard(toggleShowDashboard("ShownSavesDashboard", showDashboard))
                          handleGridMoreMenuClose();
                        }}
                      >
                        <ListItemIcon>
                          {!showDashboard ? <DashboardOffIcon fontSize="small" /> : <DashboardOnIcon fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText primary={!showDashboard ? "Show Dashboard" : "Hide Dashboard"} />
                      </StyledMenuItem>

                      {canWriteSaves(userFunctionAccess) ? (
                        <>
                          <StyledMenuItem
                            onClick={(e) => {
                              newRecord();
                              handleGridMoreMenuClose();
                            }}
                          >
                            <ListItemIcon>
                              <AddIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={"New Values"} />
                          </StyledMenuItem>

                          <StyledMenuItem
                            onClick={(e) => {
                              displayFileUploadDialog();
                              handleGridMoreMenuClose();
                            }}
                          >
                            <ListItemIcon>
                              <AddFileIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={"Upload Saves File"} />
                          </StyledMenuItem>
                        </>
                      ) : null}

                      <StyledMenuItem
                        onClick={(e) => {
                          refresh();
                          handleGridMoreMenuClose();
                        }}
                      >
                        <ListItemIcon>
                          <RefreshIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={"Refresh "} />
                      </StyledMenuItem>
                    </Menu>
                  </Box>
                </Hidden>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={12} md={showDashboard ? 8 : 12} lg={showDashboard ? 9 : 12}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                    <FormControl className={globalStyles.formControl} fullWidth={true}>
                      <InputLabel shrink={true}>Entity</InputLabel>
                      <Select
                        native
                        labelId="save-entity-key-label"
                        id="savesEntityKey"
                        name="savesEntityKey"
                        value={savesEntityKey}
                        onChange={(e) => {
                          if (e !== null) {
                            setSavesEntityKey((e.target.value as string).toLowerCase());
                          }
                          getValues(e.target.value);
                        }}
                        label="Entity"
                      >
                        <option value="">Pick Entity</option>
                        {savesEntities?.length > 0 ? (
                          <>
                            <optgroup label="Existing Entities">
                              {savesEntities
                                .filter((savesEntity) => savesEntity.dataExists === true)
                                .map((savesEntity, index) => (
                                  <option value={String(savesEntity.savesEntityKey).toLowerCase()} key={index}>
                                    {savesEntity.name}
                                  </option>
                                ))}
                            </optgroup>
                            <optgroup label="New Entities">
                              {savesEntities
                                .filter((savesEntity) => savesEntity.dataExists === false)
                                .map((savesEntity, index) => (
                                  <option value={String(savesEntity.savesEntityKey).toLowerCase()} key={index}>
                                    {savesEntity.name}
                                  </option>
                                ))}
                            </optgroup>
                          </>
                        ) : null}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                    <FormControl className={globalStyles.formControl} fullWidth={true}>
                      <InputLabel shrink={true}>Show</InputLabel>
                      <Select
                        native
                        labelId="show-indicator-label"
                        id="showIndicator"
                        name="showIndicator"
                        value={showIndicator}
                        onChange={(e) => {
                          if (e !== null) {
                            setShowIndicator(Number(e.target.value));
                            filterValues(savesEntityValues, Number(e.target.value));
                          }
                        }}
                        label="Show"
                      >
                        <option value={0}>All</option>
                        <option value={1}>Active</option>
                        <option value={2}>Inactive</option>
                        <option value={3}>Expires Soon</option>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    {savesEntityKey !== "" ? (
                      <Box className={pageStyles.datagridContainer}>
                        <CustomDataGrid uniqueGridCode={"SavesResult"} columns={columns} rows={filterSavesEntityValues} showToolBar={true} sortModel={[{ field: "savesEntityName", sort: "asc" }]} handleDataGridClick={handleDataGridClick} />
                      </Box>
                    ) : null}
                  </Grid>
                </Grid>
              </Grid>
              {
                showDashboard ?
                  <Grid item xs={12} sm={12} md={4} lg={3}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={12} lg={12}>
                        <Card elevation={0} className={globalStyles.rightCardContainer}>
                          <CardHeader title="" subheader="EXPIRATION DASHBOARD" />
                          <CardContent>
                            <Grid container style={{ width: '100%' }}>
                              {
                                savesEntityValueExpirationSummaries.map((savesEntityValueExpirationSummary, index) =>
                                  <>
                                    <Grid item xs={8} className={pageStyles.textContainer} onClick={() => gotoExpirationList(savesEntityValueExpirationSummary.savesEntityKey)}>
                                      <Typography variant="subtitle2" className={globalStyles.toggleFontColor}>
                                        {savesEntityValueExpirationSummary.savesEntityName}
                                      </Typography>
                                    </Grid>

                                    <Grid item xs={4} className={pageStyles.countContainer} onClick={() => gotoExpirationList(savesEntityValueExpirationSummary.savesEntityKey)}>
                                      <Typography variant="subtitle2" align="right" className={globalStyles.bolder} color="secondary">
                                        {savesEntityValueExpirationSummary.count}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12}><Box p={1}></Box></Grid>

                                  </>
                                )}
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                  : null
              }
            </Grid>
          </Box>
        </Screen>
      )}
    </React.Fragment>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: 0,
    },
    datagridContainer: {
      minHeight: "500px",
      height: "calc(100vh - 400px)",
      width: "100%",
      marginBottom: theme.spacing(2),
    },
    countContainer: {
      justifyItems: "right",
      cursor: "default",
    },
    textContainer: {
      justifyItems: "left",
      cursor: "default",
    },
  })
);

export default Saves;
